import type { ReactNode } from 'react'
import React, { useCallback, useEffect } from 'react'

import { ModalBox, useMediaQueryContext, useModalContext } from '@fourthwall/components'

import * as S from './AnalyticsReportLayout.styled'

export type AnalyticsLayoutProps = {
  children: ReactNode
  toolbar?: ReactNode
  sidebar?: ReactNode
  isSidebarOpen?: boolean
  hideSidebar?: () => void
}

const SidebarModal = ({ children, onClose }: { children: ReactNode; onClose?: () => void }) => (
  <ModalBox size="small" withoutPadding onClose={onClose}>
    {children}
  </ModalBox>
)

export const AnalyticsReportLayout = ({
  children,
  toolbar,
  sidebar,
  isSidebarOpen = false,
  hideSidebar,
}: AnalyticsLayoutProps) => {
  const media = useMediaQueryContext()
  const { open, close } = useModalContext()

  const closeSidebar = useCallback(() => {
    if (hideSidebar) {
      hideSidebar()
    }

    close()
  }, [hideSidebar, close])

  useEffect(() => {
    if (!media.tablet && isSidebarOpen) {
      open(SidebarModal, {
        children: sidebar,
        onClose: closeSidebar,
      })
    }
  }, [open, isSidebarOpen, sidebar, media.tablet, hideSidebar, closeSidebar])

  const renderSidebar = () => {
    if (!sidebar || !media.tablet) {
      return null
    }

    return (
      <S.SidebarTrack visible={isSidebarOpen}>
        <S.SidebarPositioner>
          <S.SidebarPanel visible={isSidebarOpen}>{sidebar}</S.SidebarPanel>
        </S.SidebarPositioner>
      </S.SidebarTrack>
    )
  }

  return (
    <S.Container>
      {toolbar}
      <S.Inner>
        <S.Content hasAside={media.tablet && !!sidebar && isSidebarOpen}>{children}</S.Content>
        {renderSidebar()}
      </S.Inner>
    </S.Container>
  )
}
